import { useEffect, useState } from 'react';
import {
  fetchCatalogDatasets,
  fetchCatalogProcesses,
  type CatalogDatasetsResponse,
  type CatalogProcess,
  type CatalogProcessesResponse,
} from '@/lib/sb-api-client';

/**
 * useCatalog — read the platform's own registry so the docs never hardcode
 * facts that live in the catalog. Fetches /catalog/datasets and
 * /catalog/processes client-side.
 *
 * Doctrine: on fetch failure we expose a flag and leave every derived count
 * `null`. Pages render "live figures temporarily unavailable" — NEVER a
 * hardcoded fallback number. Each source fails independently (allSettled), so
 * a hiccup on one endpoint doesn't blank the other.
 */
export interface UseCatalogResult {
  loading: boolean;
  /** True when at least one of the two catalog fetches failed. */
  error: boolean;
  datasetsError: boolean;
  processesError: boolean;
  datasets: CatalogDatasetsResponse | null;
  processes: CatalogProcessesResponse | null;
  /** Total OGC processes exposed by the API, or null if unavailable. */
  processCount: number | null;
  /** Distinct dataset domains tracked in the registry, or null. */
  domainCount: number | null;
  /** Total schemas tracked in the registry, or null. */
  schemaCount: number | null;
  /** How many processes are wired as Claude (MCP) tools, or null. */
  mcpToolCount: number | null;
  /** Processes grouped by their `category` field (empty until loaded). */
  processesByCategory: Record<string, CatalogProcess[]>;
}

function groupByCategory(
  processes: CatalogProcess[] | undefined,
): Record<string, CatalogProcess[]> {
  const grouped: Record<string, CatalogProcess[]> = {};
  if (!processes) return grouped;
  for (const process of processes) {
    const key = process.category ?? 'other';
    (grouped[key] ??= []).push(process);
  }
  return grouped;
}

export function useCatalog(): UseCatalogResult {
  const [loading, setLoading] = useState(true);
  const [datasets, setDatasets] = useState<CatalogDatasetsResponse | null>(null);
  const [processes, setProcesses] = useState<CatalogProcessesResponse | null>(null);
  const [datasetsError, setDatasetsError] = useState(false);
  const [processesError, setProcessesError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      const [datasetsResult, processesResult] = await Promise.allSettled([
        fetchCatalogDatasets(),
        fetchCatalogProcesses(),
      ]);
      if (cancelled) return;

      if (datasetsResult.status === 'fulfilled') {
        setDatasets(datasetsResult.value);
        setDatasetsError(false);
      } else {
        setDatasets(null);
        setDatasetsError(true);
      }

      if (processesResult.status === 'fulfilled') {
        setProcesses(processesResult.value);
        setProcessesError(false);
      } else {
        setProcesses(null);
        setProcessesError(true);
      }

      setLoading(false);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const processCount =
    processes?.summary.total_processes ?? processes?.processes.length ?? null;

  const domainCount = datasets
    ? new Set(datasets.datasets.map((dataset) => dataset.domain)).size
    : null;

  // distinct_schemas, not total_schemas_tracked: the latter counts registry
  // ROWS (95 in prod) while several rows legitimately share one schema — 7
  // resolve to verkeersmodel_nrm, 5 to netwerk_gtfs_v9437 — so labelling it
  // "schemas" overstated by 21. Falls back to the old field for an sb-api
  // older than the lane-aware catalog (PR #25).
  const schemaCount =
    datasets?.summary.distinct_schemas ??
    datasets?.summary.total_schemas_tracked ??
    null;

  const mcpToolCount = processes
    ? processes.processes.filter((process) => Boolean(process.mcp_name)).length
    : null;

  return {
    loading,
    error: datasetsError || processesError,
    datasetsError,
    processesError,
    datasets,
    processes,
    processCount,
    domainCount,
    schemaCount,
    mcpToolCount,
    processesByCategory: groupByCategory(processes?.processes),
  };
}
